const STOCK_SORTED_AVAILABILITY_ENDPOINT = '/apps/stock-sorted/availability';
const STOCK_SORTED_STREAM_HOST = 'stock.sorted.fast';
const STOCK_SORTED_STREAM_PATH = '/storefront/availability/stream';
const STOCK_SORTED_BACKSTOP_MS = 10_000;
const STOCK_SORTED_STREAM_RETRY_MS = 30_000;
const PRODUCT_SELECT_EVENT = 'shopify:product:select';
const SECTION_LOAD_EVENT = 'shopify:section:load';
const STATUS_SELECTOR = 'rx-limited-edition-status[data-live-count-contract="stock-sorted-app-proxy"]';

/**
 * Return a decimal Shopify resource ID without coercing it through Number.
 *
 * @param {unknown} value
 * @param {'Product' | 'ProductVariant'} resource
 * @returns {string | null}
 */
function shopifyResourceId(value, resource) {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
  }

  if (typeof value !== 'string') return null;

  const match = value.trim().match(new RegExp(`^(?:gid://shopify/${resource}/)?([1-9]\\d*)$`));
  return match?.[1] ?? null;
}

/**
 * @param {unknown} value
 * @returns {number | null}
 */
function positiveSafeInteger(value) {
  if (typeof value !== 'string' || !/^[1-9]\d*$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

/**
 * @param {unknown} value
 * @returns {string | null}
 */
function positiveDecimalIdentifier(value) {
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
  }

  return typeof value === 'string' && /^[1-9]\d*$/.test(value) ? value : null;
}

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Only Stock Sorted's exact HTTPS stream route with one nonblank token is accepted.
 *
 * @param {unknown} value
 * @returns {{ valid: boolean, url: string | null }}
 */
function verifiedStreamUrl(value) {
  if (value === undefined || value === null || value === '') {
    return { valid: true, url: null };
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return { valid: false, url: null };
  }

  try {
    const url = new URL(value, window.location.origin);
    const tokens = url.searchParams.getAll('token');
    const queryKeys = [...url.searchParams.keys()];
    const isVerifiedStream =
      url.protocol === 'https:' &&
      url.hostname === STOCK_SORTED_STREAM_HOST &&
      url.port === '' &&
      url.username === '' &&
      url.password === '' &&
      url.pathname === STOCK_SORTED_STREAM_PATH &&
      url.hash === '' &&
      tokens.length === 1 &&
      tokens[0].trim() !== '' &&
      queryKeys.length === 1 &&
      queryKeys[0] === 'token';

    return isVerifiedStream ? { valid: true, url: url.href } : { valid: false, url: null };
  } catch {
    return { valid: false, url: null };
  }
}

/**
 * Validate the complete ratio-1 pool contract returned by the official proxy.
 * No value from an invalid or partial payload reaches the visible count.
 *
 * @param {unknown} payload
 * @param {{ poolKey: string, capacity: number, variantGid: string }} expected
 * @returns {{ available: number, streamUrl: string | null } | null}
 */
function validateStockSortedAvailability(payload, expected) {
  if (!isRecord(payload)) return null;
  if (payload.enabled !== true || payload.managed !== true) return null;
  const expectedVariantId = shopifyResourceId(expected.variantGid, 'ProductVariant');
  const selectedVariantId = shopifyResourceId(payload.selected_variant_id, 'ProductVariant');
  if (!expectedVariantId || selectedVariantId !== expectedVariantId) return null;
  if (!Number.isSafeInteger(payload.available)) return null;
  if (payload.available < 0 || payload.available > expected.capacity) return null;
  if (!Array.isArray(payload.pools) || payload.pools.length !== 1) return null;

  const pool = payload.pools[0];
  if (!isRecord(pool)) return null;
  const responsePoolId = positiveDecimalIdentifier(pool.id);
  const configuredPoolId = positiveDecimalIdentifier(expected.poolKey);
  if (!configuredPoolId || responsePoolId !== configuredPoolId) return null;
  if (pool.consumption_ratio !== 1) return null;
  if (!Number.isSafeInteger(pool.available) || pool.available !== payload.available) return null;
  if (pool.available < 0 || pool.available > expected.capacity) return null;
  if (!Number.isSafeInteger(pool.total_quantity) || pool.total_quantity !== payload.available) return null;
  if (pool.total_quantity < 0 || pool.total_quantity > expected.capacity) return null;

  const stream = verifiedStreamUrl(payload.stream_url);
  if (!stream.valid) return null;

  return { available: payload.available, streamUrl: stream.url };
}

/**
 * @param {string} template
 * @param {Record<string, string | number>} tokens
 * @returns {string}
 */
function formatTemplate(template, tokens) {
  return Object.entries(tokens).reduce(
    (output, [token, value]) => output.replaceAll(`{${token}}`, String(value)),
    template
  );
}

class RxLimitedEditionStatus extends HTMLElement {
  /** @type {{ productId: string, poolKey: string, capacity: number, label: string, countTemplate: string, ariaTemplate: string, variantIds: Set<string> } | null} */
  #contract = null;

  /** @type {string | null} */
  #activeVariantId = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {EventSource | null} */
  #eventSource = null;

  /** @type {string | null} */
  #eventSourceVariantGid = null;

  /** @type {Element | null} */
  #eventRoot = null;

  /** @type {number | null} */
  #backstopTimer = null;

  #requestVersion = 0;

  #selectionPending = false;

  #streamRetryAfter = 0;

  connectedCallback() {
    this.#selectionPending = false;
    this.#activeVariantId = null;
    this.#contract = this.#readContract();
    this.#clearCount();

    if (!this.#contract) return;

    this.#eventRoot = this.closest('.shopify-section, dialog');
    this.#eventRoot?.addEventListener(PRODUCT_SELECT_EVENT, this.#handleProductSelect);
    this.#backstopTimer = window.setInterval(this.#handleBackstop, STOCK_SORTED_BACKSTOP_MS);
    this.refreshFromCurrentVariant(true);
  }

  disconnectedCallback() {
    this.#selectionPending = false;
    this.#eventRoot?.removeEventListener(PRODUCT_SELECT_EVENT, this.#handleProductSelect);
    this.#eventRoot = null;

    if (this.#backstopTimer !== null) {
      window.clearInterval(this.#backstopTimer);
      this.#backstopTimer = null;
    }

    this.#invalidatePendingWork();
  }

  /**
   * Re-resolve the selected variant after a section reload or as a polling backstop.
   *
   * @param {boolean} clearBeforeFetch
   */
  refreshFromCurrentVariant(clearBeforeFetch = false) {
    if (!this.#contract) {
      this.#clearCount();
      return;
    }

    const variantId = this.#currentVariantId();
    if (!variantId || !this.#contract.variantIds.has(variantId)) {
      this.#invalidatePendingWork();
      this.#activeVariantId = null;
      this.#clearCount();
      return;
    }

    this.#fetchAvailability(variantId, clearBeforeFetch || variantId !== this.#activeVariantId);
  }

  /**
   * @returns {{ productId: string, poolKey: string, capacity: number, label: string, countTemplate: string, ariaTemplate: string, variantIds: Set<string> } | null}
   */
  #readContract() {
    if (this.dataset.limitedVerified !== 'true' || this.dataset.poolVerified !== 'true') return null;
    if (this.dataset.capacityType !== 'number_integer') return null;

    const productId = shopifyResourceId(this.dataset.productId, 'Product');
    const poolKey = positiveDecimalIdentifier(this.dataset.poolKey);
    const capacity = positiveSafeInteger(this.dataset.editionCapacity);
    const label = this.dataset.limitedLabel?.trim() ?? '';
    const countTemplate = this.dataset.countTemplate?.trim() ?? '';
    const ariaTemplate = this.dataset.ariaTemplate?.trim() ?? '';
    const renderedLabel = this.querySelector('.rx-edition-status__label')?.textContent?.trim() ?? '';

    if (!productId || !poolKey || !capacity || !label || renderedLabel !== label) return null;
    if (!countTemplate.includes('{remaining}') || !countTemplate.includes('{capacity}')) return null;
    if (!ariaTemplate.includes('{label}') || !ariaTemplate.includes('{remaining}') || !ariaTemplate.includes('{capacity}')) {
      return null;
    }

    let rawVariantIds;
    try {
      rawVariantIds = JSON.parse(this.dataset.productVariantIds ?? '');
    } catch {
      return null;
    }

    if (!Array.isArray(rawVariantIds) || rawVariantIds.length === 0) return null;

    const variantIds = new Set();
    for (const rawVariantId of rawVariantIds) {
      const variantId = shopifyResourceId(rawVariantId, 'ProductVariant');
      if (!variantId || variantIds.has(variantId)) return null;
      variantIds.add(variantId);
    }

    return { productId, poolKey, capacity, label, countTemplate, ariaTemplate, variantIds };
  }

  /** @returns {string | null} */
  #currentVariantId() {
    if (!this.#contract) return null;

    const productScope = this.closest('.product-information') ?? this.closest('.shopify-section') ?? document;
    const variantInput = productScope.querySelector('product-form-component [ref="variantId"][name="id"]');
    const checkedOption = productScope.querySelector('variant-picker input:checked[data-variant-id]');
    const selectedOption = productScope.querySelector('variant-picker option:checked[data-variant-id]');
    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    const candidates = [
      variantInput instanceof HTMLInputElement ? variantInput.value : null,
      checkedOption instanceof HTMLElement ? checkedOption.dataset.variantId : null,
      selectedOption instanceof HTMLElement ? selectedOption.dataset.variantId : null,
      urlVariantId,
      this.#activeVariantId,
      this.dataset.initialVariantId,
    ];

    for (const candidate of candidates) {
      const variantId = shopifyResourceId(candidate, 'ProductVariant');
      if (variantId && this.#contract.variantIds.has(variantId)) return variantId;
    }

    return null;
  }

  /**
   * @param {Event & { product?: { id?: unknown }, detail?: { variantId?: unknown }, promise?: Promise<unknown> }} event
   */
  #handleProductSelect = (event) => {
    if (!this.#contract || !(event.target instanceof Element)) return;
    if (event.target.closest('product-card')) return;

    const eventProductId = shopifyResourceId(event.product?.id, 'Product');
    if (eventProductId && eventProductId !== this.#contract.productId) {
      this.#selectionPending = false;
      this.#invalidatePendingWork();
      this.#activeVariantId = null;
      this.#clearCount();
      return;
    }

    const synchronousVariantId = shopifyResourceId(event.detail?.variantId, 'ProductVariant');
    this.#selectionPending = true;
    this.#invalidatePendingWork();
    this.#activeVariantId =
      synchronousVariantId && this.#contract.variantIds.has(synchronousVariantId) ? synchronousVariantId : null;
    this.#clearCount();

    const selectionVersion = this.#requestVersion;
    if (!event.promise || typeof event.promise.then !== 'function') {
      this.#selectionPending = false;
      if (this.#activeVariantId) this.#fetchAvailability(this.#activeVariantId, true);
      return;
    }

    event.promise
      .then((result) => {
        if (!this.isConnected || selectionVersion !== this.#requestVersion || !this.#contract) return;

        this.#selectionPending = false;

        const resolved = isRecord(result) ? result : null;
        const detail = resolved && isRecord(resolved.detail) ? resolved.detail : null;
        const resource = detail && isRecord(detail.resource) ? detail.resource : null;
        const variant = resolved && isRecord(resolved.variant) ? resolved.variant : null;
        const resolvedVariantId = shopifyResourceId(resource?.id ?? variant?.id, 'ProductVariant');

        if (!resolvedVariantId || !this.#contract.variantIds.has(resolvedVariantId)) {
          this.#activeVariantId = null;
          this.#clearCount();
          return;
        }

        this.#fetchAvailability(resolvedVariantId, true);
      })
      .catch(() => {
        if (selectionVersion !== this.#requestVersion) return;
        this.#selectionPending = false;
        this.#activeVariantId = null;
        this.#clearCount();
      });
  };

  #handleBackstop = () => {
    if (this.#selectionPending) return;
    this.refreshFromCurrentVariant(false);
  };

  /**
   * @param {string} variantId
   * @param {boolean} clearBeforeFetch
   */
  async #fetchAvailability(variantId, clearBeforeFetch) {
    if (!this.#contract || !this.#contract.variantIds.has(variantId)) {
      this.#clearCount();
      return;
    }

    if (clearBeforeFetch) this.#clearCount();
    if (variantId !== this.#activeVariantId) this.#stopEventSource();

    this.#activeVariantId = variantId;
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const controller = this.#abortController;
    const requestVersion = ++this.#requestVersion;
    const variantGid = `gid://shopify/ProductVariant/${variantId}`;
    const url = new URL(STOCK_SORTED_AVAILABILITY_ENDPOINT, window.location.origin);
    url.searchParams.set('variant_id', variantGid);

    this.setAttribute('aria-busy', 'true');
    const timeout = window.setTimeout(() => controller.abort(), STOCK_SORTED_BACKSTOP_MS);

    try {
      const response = await fetch(`${url.pathname}${url.search}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      });

      if (!response.ok) throw new Error('Stock Sorted availability request failed');
      const payload = await response.json();
      if (requestVersion !== this.#requestVersion || variantId !== this.#activeVariantId || !this.#contract) return;

      const verified = validateStockSortedAvailability(payload, {
        poolKey: this.#contract.poolKey,
        capacity: this.#contract.capacity,
        variantGid,
      });

      if (!verified) {
        this.#clearCount();
        this.#stopEventSource();
        return;
      }

      this.#showCount(verified.available);
      this.#syncEventSource(verified.streamUrl, variantGid);
    } catch {
      if (requestVersion !== this.#requestVersion) return;
      this.#clearCount();
      this.#stopEventSource();
    } finally {
      window.clearTimeout(timeout);
      if (requestVersion === this.#requestVersion) {
        this.removeAttribute('aria-busy');
        if (this.#abortController === controller) this.#abortController = null;
      }
    }
  }

  /**
   * @param {string | null} streamUrl
   * @param {string} variantGid
   */
  #syncEventSource(streamUrl, variantGid) {
    if (!streamUrl) {
      this.#stopEventSource();
      this.#streamRetryAfter = 0;
      return;
    }

    if (this.#eventSource?.url === streamUrl && this.#eventSourceVariantGid === variantGid) {
      return;
    }

    if (Date.now() < this.#streamRetryAfter) return;

    this.#stopEventSource();
    if (typeof EventSource !== 'function') {
      this.#streamRetryAfter = Number.POSITIVE_INFINITY;
      return;
    }

    try {
      const source = new EventSource(streamUrl);
      this.#eventSource = source;
      this.#eventSourceVariantGid = variantGid;

      source.addEventListener('inventory', () => this.#handleInventorySignal(source, variantGid));
      source.addEventListener('error', () => {
        if (source !== this.#eventSource) return;
        this.#handleStreamFailure();
      });
    } catch {
      this.#handleStreamFailure();
    }
  }

  /**
   * @param {EventSource} source
   * @param {string} variantGid
   */
  #handleInventorySignal(source, variantGid) {
    if (!this.#contract || source !== this.#eventSource || variantGid !== this.#eventSourceVariantGid) return;

    const variantId = shopifyResourceId(variantGid, 'ProductVariant');
    if (!variantId || !this.#contract.variantIds.has(variantId)) {
      this.#handleStreamFailure();
      return;
    }

    // The stream is an invalidation signal only. Never paint event data.
    // Re-read and fully validate the signed app-proxy contract before showing
    // a new numerator, matching Stock Sorted's own widget behaviour.
    this.#fetchAvailability(variantId, false);
  }

  /** @param {number} available */
  #showCount(available) {
    if (!this.#contract) return;

    const content = this.querySelector('.rx-edition-status__content');
    const count = this.querySelector('.rx-edition-status__count');
    if (!(content instanceof HTMLElement) || !(count instanceof HTMLElement)) {
      this.#clearCount();
      return;
    }

    count.textContent = formatTemplate(this.#contract.countTemplate, {
      remaining: available,
      capacity: this.#contract.capacity,
    });
    count.hidden = false;
    count.removeAttribute('aria-hidden');
    content.setAttribute(
      'aria-label',
      formatTemplate(this.#contract.ariaTemplate, {
        label: this.#contract.label,
        remaining: available,
        capacity: this.#contract.capacity,
      })
    );
    this.classList.toggle('rx-edition-status--closed', available === 0);
    this.dataset.editionSource = 'stock-sorted-app-proxy';
    this.dataset.editionCountVerified = 'true';
    this.dataset.editionSize = String(this.#contract.capacity);
    this.dataset.stockSortedAvailable = String(available);
  }

  #clearCount() {
    const content = this.querySelector('.rx-edition-status__content');
    const count = this.querySelector('.rx-edition-status__count');
    const label = this.#contract?.label ?? this.dataset.limitedLabel?.trim() ?? '';

    if (count instanceof HTMLElement) {
      count.textContent = '';
      count.hidden = true;
      count.setAttribute('aria-hidden', 'true');
    }
    if (content instanceof HTMLElement && label) content.setAttribute('aria-label', label);

    this.classList.remove('rx-edition-status--closed');
    this.dataset.editionSource = 'verified-label';
    this.dataset.editionCountVerified = 'false';
    delete this.dataset.editionSize;
    delete this.dataset.stockSortedAvailable;
  }

  #invalidatePendingWork() {
    this.#requestVersion += 1;
    this.#streamRetryAfter = 0;
    this.#abortController?.abort();
    this.#abortController = null;
    this.removeAttribute('aria-busy');
    this.#stopEventSource();
  }

  #stopEventSource() {
    this.#eventSource?.close();
    this.#eventSource = null;
    this.#eventSourceVariantGid = null;
  }

  #handleStreamFailure() {
    this.#clearCount();
    this.#stopEventSource();
    this.#streamRetryAfter = Date.now() + STOCK_SORTED_STREAM_RETRY_MS;
  }
}

if (!customElements.get('rx-limited-edition-status')) {
  customElements.define('rx-limited-edition-status', RxLimitedEditionStatus);
}

document.addEventListener(SECTION_LOAD_EVENT, (event) => {
  if (!(event.target instanceof Element)) return;

  const statuses = [
    ...(event.target.matches(STATUS_SELECTOR) ? [event.target] : []),
    ...event.target.querySelectorAll(STATUS_SELECTOR),
  ];

  for (const status of statuses) {
    if (status instanceof RxLimitedEditionStatus) status.refreshFromCurrentVariant(true);
  }
});

export { validateStockSortedAvailability, verifiedStreamUrl };
